import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile } from 'node:fs/promises'
import { join } from 'node:path'
import { homedir } from 'node:os'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'
import { isNpxPackageAvailable } from './npx-check.js'

const execFileAsync = promisify(execFile)

/** Official Grok Build install location (curl installer), in addition to PATH. */
const HOME_GROK = join(homedir(), '.grok', 'bin', 'grok')

const NPX_PACKAGE = '@xai-official/grok'

/**
 * Launcher for xAI Grok Build (the `grok` TUI).
 *
 * System prompt is passed with `--rules` (alias `--append-system-prompt`) so
 * we never write AGENTS.md into the candidate workspace. Do not pass a
 * positional prompt or `-p/--single` — those start a turn or headless mode.
 */
export class GrokLauncher implements ProviderLauncher {
  readonly name = 'grok'
  readonly displayName = 'Grok (xAI)'
  readonly pricingHint = '💰 SuperGrok / X Premium+ (essai gratuit, ou clé API xAI)'

  async isInstalled(): Promise<boolean> {
    return (await this.resolveInstalledBinary()) !== null || isNpxPackageAvailable(NPX_PACKAGE)
  }

  async getVersion(): Promise<string | null> {
    const binary = await this.resolveInstalledBinary()
    if (!binary) return null
    try {
      const { stdout } = await execFileAsync(binary, ['--version'])
      return stdout.trim()
    } catch {
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt =
      config.systemPromptContent ?? (await readFile(config.systemPromptPath, 'utf-8'))

    const args: string[] = []

    args.push('--cwd', workdir)
    // Append interview rules. Do not use --system-prompt-override — that
    // replaces Grok's default tool instructions and breaks the TUI agent.
    args.push('--rules', systemPrompt)

    if (config.model) {
      args.push('-m', config.model)
    }

    const permissionMode = config.permissionMode ?? 'default'
    args.push('--permission-mode', permissionMode)
    if (permissionMode === 'bypassPermissions') {
      args.push('--always-approve')
    }

    if (config.disallowedTools?.some((tool) => /web[_-]?search/i.test(tool))) {
      args.push('--disable-web-search')
    }

    const recorder = new SessionRecorder()

    const binaryName = await this.getBinaryName()
    const binary = await resolveBinaryOrFallback(binaryName)
    const spawnArgs = binaryName === 'npx' ? [NPX_PACKAGE, ...args] : args

    const ptyProc = getPty().spawn(binary, spawnArgs, {
      name: 'xterm-256color',
      cwd: workdir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(({ exitCode, signal }) => {
            cleanup()
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
      },
      recorder,
    }
  }

  private async resolveInstalledBinary(): Promise<string | null> {
    for (const binary of ['grok', HOME_GROK]) {
      try {
        await execFileAsync(binary, ['--version'])
        return binary
      } catch {
        // try next location
      }
    }
    return null
  }

  private async getBinaryName(): Promise<string> {
    return (await this.resolveInstalledBinary()) ?? 'npx'
  }
}
